import { getApiV1Base } from "./apiConfig";
import {
  clearTokens,
  getAccessToken,
  getRefreshToken,
  setAccessToken,
} from "./authTokens";

let refreshPromise = null;

async function refreshAccessToken() {
  const rt = getRefreshToken();
  if (!rt) {
    clearTokens();
    return null;
  }
  if (refreshPromise) {
    return refreshPromise;
  }
  refreshPromise = (async () => {
    const res = await fetch(`${getApiV1Base()}/auth/refresh`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ refresh_token: rt }),
    });
    if (!res.ok) {
      clearTokens();
      return null;
    }
    const data = await res.json();
    const next = data?.access_token;
    if (typeof next === "string" && next) {
      setAccessToken(next);
      return next;
    }
    clearTokens();
    return null;
  })().finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
}

/**
 * JSON API request under `/api/v1`. Retries once after refresh on 401 when `auth` is true.
 */
export async function apiRequest(path, options = {}) {
  const { method = "GET", body, auth = true, headers: extraHeaders = {} } =
    options;
  const base = getApiV1Base();
  const url = `${base}${path.startsWith("/") ? path : `/${path}`}`;

  async function send(bearer) {
    const headers = { Accept: "application/json", ...extraHeaders };
    if (auth && bearer) {
      headers.Authorization = `Bearer ${bearer}`;
    }
    const init = { method, headers };
    if (body != null) {
      headers["Content-Type"] = "application/json";
      init.body = typeof body === "string" ? body : JSON.stringify(body);
    }
    return fetch(url, init);
  }

  const bearer = auth ? getAccessToken() : null;
  let res = await send(bearer);

  if (res.status === 401 && auth) {
    const next = await refreshAccessToken();
    if (next) {
      res = await send(next);
    }
  }

  return res;
}

export async function fetchCurrentUser() {
  const res = await apiRequest("/auth/me", { method: "GET", auth: true });
  if (res.status === 401) {
    clearTokens();
    const err = new Error("Unauthorized");
    err.status = 401;
    throw err;
  }
  if (!res.ok) {
    const err = new Error(`Request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

export async function postLogout() {
  const rt = getRefreshToken();
  if (!rt) return;
  try {
    await fetch(`${getApiV1Base()}/auth/logout`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ refresh_token: rt }),
    });
  } catch {
    /* ignore network errors — still clear local session */
  }
}

/** Bearer for one-off fetch (e.g. receive funding). */
export function getAuthorizationHeader() {
  const t = getAccessToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}
